"use client"

import { useState } from "react"
import { motion, Variants } from "framer-motion"
import Image from "next/image"
import Header from "@/components/header"
import Footer from "@/components/footer"
import {
    Coins,
    Globe,
    Layers,
    Vote,
    Shield,
    TrendingUp,
    Lock,
    Rocket,
    Code2,
    FileCode,
    Zap,
    BarChart3,
    Users,
    ArrowRight,
    Copy,
    Check,
    ExternalLink,
    CircleDollarSign,
    Search,
    Megaphone,
    Building2,
    ChevronRight,
    BookOpen,
    LucideIcon,
} from "lucide-react"

/* ─────────────────────── ANIMATION VARIANTS ─────────────────────── */

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
}

const staggerContainer: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
}

/* ─────────────────────── DATA ─────────────────────── */

const tokenInfo = {
    name: "TokenKickstarter",
    symbol: "TKS",
    decimals: 18,
    totalSupply: "1B / network",
    networks: ["BSC", "Ethereum"],
    upcomingNetworks: ["Polygon", "Base"],
}

const tokenomics = [
    { label: "Presale", pct: 40, color: "bg-emerald-500" },
    { label: "Staking Rewards", pct: 20, color: "bg-blue-500" },
    { label: "Ecosystem Fund", pct: 15, color: "bg-violet-500" },
    { label: "Team & Advisors", pct: 10, color: "bg-amber-500" },
    { label: "Marketing", pct: 10, color: "bg-pink-500" },
    { label: "Liquidity Pool", pct: 5, color: "bg-cyan-500" },
]

const stakingTiers = [
    { duration: "30 Days", apy: "8%", min: "1,000 TKS" },
    { duration: "90 Days", apy: "15%", min: "5,000 TKS" },
    { duration: "180 Days", apy: "25%", min: "10,000 TKS" },
    { duration: "365 Days", apy: "40%", min: "25,000 TKS" },
]

const contracts = {
    mainnet: {
        token: [
            { network: "BSC", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4", explorer: "https://etherscan.io" },
        ],
        vault: [
            { network: "BSC", address: "0xa79289DB22eB16013d8044a64050691f4F3261C1", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0xa79289DB22eB16013d8044a64050691f4F3261C1", explorer: "https://etherscan.io" },
        ],
        collector: [
            { network: "BSC", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://basescan.org" },
        ],
        staking: [
            { network: "BSC", address: "0x6e3eb266d957a639982C4A47ec46586218cC1501", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x6e3eb266d957a639982C4A47ec46586218cC1501", explorer: "https://etherscan.io" },
        ],
        presaleFactory: [
            { network: "BSC", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://basescan.org" },
        ],
        tokenLocker: [
            { network: "BSC", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://basescan.org" },
        ],
        deployerProxy: [
            { network: "BSC", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://basescan.org" },
        ],
        fairLaunchFactory: [
            { network: "BSC", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://basescan.org" },
        ],
        collectorFactory: [
            { network: "BSC", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://basescan.org" },
        ],
    },
    testnet: {
        token: [
            { network: "BSC Testnet", address: "0x8664E4345E2181032F1dDfbCCE210456C0eD8401", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x8664E4345E2181032F1dDfbCCE210456C0eD8401", explorer: "https://sepolia.etherscan.io" },
        ],
        vault: [
            { network: "BSC Testnet", address: "0x38b95390f456db4E5308AfB455Ce6d643f7263bd", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x38b95390f456db4E5308AfB455Ce6d643f7263bd", explorer: "https://sepolia.etherscan.io" },
        ],
        collector: [
            { network: "BSC Testnet", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://sepolia.basescan.org" },
        ],
        staking: [
            { network: "BSC Testnet", address: "0xf035f4906fF6Eb79C2E183C307729F1f517EA366", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0xf035f4906fF6Eb79C2E183C307729F1f517EA366", explorer: "https://sepolia.etherscan.io" },
        ],
        presaleFactory: [
            { network: "BSC Testnet", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://sepolia.basescan.org" },
        ],
        tokenLocker: [
            { network: "BSC Testnet", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://sepolia.basescan.org" },
        ],
        deployerProxy: [
            { network: "BSC Testnet", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://sepolia.basescan.org" },
        ],
        fairLaunchFactory: [
            { network: "BSC Testnet", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://sepolia.basescan.org" },
        ],
        collectorFactory: [
            { network: "BSC Testnet", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://sepolia.basescan.org" },
        ],
    },
}

/* ─────────────────────── TABLE OF CONTENTS ─────────────────────── */

const tocItems = [
    { id: "abstract", label: "Abstract", icon: BookOpen },
    { id: "token-details", label: "Token Details", icon: Coins },
    { id: "tokenomics", label: "Tokenomics", icon: BarChart3 },
    { id: "staking", label: "Staking", icon: TrendingUp },
    { id: "create-presale", label: "Create Presale", icon: Rocket },
    { id: "create-token", label: "Create Token", icon: Code2 },
    { id: "token-locking", label: "Token Locking", icon: Lock },
    { id: "listing-directory", label: "Listing Directory", icon: Search },
    { id: "smart-contracts", label: "Smart Contracts", icon: FileCode },
    { id: "architecture", label: "Architecture", icon: Layers },
    { id: "revenue", label: "Revenue & Ecosystem", icon: CircleDollarSign },
]

/* ─────────────────────── REUSABLE SUB-COMPONENTS ─────────────────────── */

function SectionHeading({ id, icon: Icon, badge, title, titleHighlight, description }: {
    id: string
    icon: LucideIcon
    badge: string
    title: string
    titleHighlight: string
    description: string
}) {
    return (
        <motion.div
            id={id}
            className="mb-12 text-center scroll-mt-28"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
        >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                <Icon className="h-4 w-4" />
                {badge}
            </div>
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
                {title}{" "}
                <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                    {titleHighlight}
                </span>
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{description}</p>
        </motion.div>
    )
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-semibold">{value}</span>
        </div>
    )
}

function AddressRow({ network, address, explorer }: { network: string; address: string; explorer: string }) {
    const [copied, setCopied] = useState(false)
    const handleCopy = () => {
        navigator.clipboard.writeText(address)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    const short = `${address.slice(0, 6)}...${address.slice(-4)}`

    return (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/20 group hover:bg-muted/40 transition-all">
            <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap min-w-[80px]">{network}</span>
                <code className="text-sm font-mono truncate hidden sm:block">{address}</code>
                <code className="text-sm font-mono sm:hidden">{short}</code>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={handleCopy} className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all" title="Copy address">
                    {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <a href={`${explorer}/address/${address}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all" title="View on explorer">
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            </div>
        </div>
    )
}

function FeatureCard({ icon: Icon, title, description }: { icon: LucideIcon; title: string; description: string }) {
    return (
        <motion.div variants={fadeUp} className="glass-card p-6 group hover:border-primary/40 transition-all">
            <div className="mb-4 h-12 w-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:from-primary/30 group-hover:to-primary/10 transition-all group-hover:scale-110">
                <Icon className="h-6 w-6 text-primary" />
            </div>
            <h4 className="font-bold mb-2">{title}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </motion.div>
    )
}

/* ─────────────────────── MAIN PAGE ─────────────────────── */

export function WhitepaperContent() {
    return (
        <div className="min-h-screen flex flex-col relative">
            <Header />
            <main className="flex-1 relative z-10">
                {/* ═══ HERO ═══ */}
                <section className="pt-32 pb-16 md:pt-40 md:pb-20 relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
                    </div>
                    <div className="container relative z-10 text-center">
                        <motion.div
                            className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <BookOpen className="h-4 w-4" />
                            Official Whitepaper
                        </motion.div>
                        <motion.h1
                            className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                        >
                            Token
                            <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                                Kickstarter
                            </span>
                        </motion.h1>
                        <motion.p
                            className="mx-auto max-w-3xl text-lg sm:text-xl text-muted-foreground leading-relaxed"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                        >
                            A decentralized, multi-chain launchpad platform revolutionizing how crypto projects
                            are created, funded, and launched. This whitepaper outlines the technology,
                            tokenomics, and vision behind TKS.
                        </motion.p>

                        {/* Table of Contents */}
                        <motion.div
                            className="mt-12 glass-card p-6 sm:p-8 max-w-2xl mx-auto text-left"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                <Layers className="h-5 w-5 text-primary" />
                                Table of Contents
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {tocItems.map((item, i) => {
                                    const TocIcon = item.icon
                                    return (
                                        <a
                                            key={item.id}
                                            href={`#${item.id}`}
                                            className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/40 transition-all group text-sm"
                                        >
                                            <span className="text-xs text-muted-foreground w-5 text-right shrink-0">
                                                {i + 1}.
                                            </span>
                                            <TocIcon className="h-4 w-4 text-primary shrink-0" />
                                            <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                                                {item.label}
                                            </span>
                                            <ChevronRight className="h-3 w-3 ml-auto text-muted-foreground/50 group-hover:text-primary transition-colors" />
                                        </a>
                                    )
                                })}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 1. ABSTRACT ═══ */}
                <section className="py-16 md:py-20" id="abstract">
                    <div className="container">
                        <SectionHeading
                            id=""
                            icon={BookOpen}
                            badge="Section 1"
                            title=""
                            titleHighlight="Abstract"
                            description=""
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="prose prose-lg dark:prose-invert max-w-none">
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
                                    TokenKickstarter (TKS) is a <strong className="text-foreground">decentralized, multi-chain launchpad platform</strong> designed
                                    to democratize access to token creation, presale fundraising, and DeFi infrastructure.
                                    Built on Ethereum and Binance Smart Chain with plans to expand to Base and Polygon,
                                    TKS provides a comprehensive suite of smart contract-powered tools accessible to everyone — from
                                    individual developers to enterprise-level projects.
                                </p>
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg mt-4">
                                    Our mission is to lower the barrier to entry for blockchain projects while maintaining
                                    the highest security standards through audited smart contracts, KYC verification, and
                                    transparent on-chain operations. The TKS token powers the entire ecosystem, providing
                                    reduced fees, staking rewards, governance rights, and priority access to new features.
                                </p>
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg mt-4">
                                    Beyond the platform itself, TKS is building a <strong className="text-foreground">revenue-driven ecosystem</strong> by
                                    establishing strategic subsidiary ventures that generate real-world revenue. This revenue is continuously
                                    funneled back into the TKS ecosystem — funding marketing, product development, and
                                    buy-back mechanisms that increase token value over time.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 2. TOKEN DETAILS ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="token-details"
                            icon={Coins}
                            badge="Section 2"
                            title="Token"
                            titleHighlight="Details"
                            description="TKS is the native utility token powering the TokenKickstarter ecosystem."
                        />
                        <motion.div
                            className="grid gap-8 lg:grid-cols-2 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <motion.div variants={fadeUp} className="glass-card p-8">
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                    <Coins className="h-5 w-5 text-primary" />
                                    Token Information
                                </h3>
                                <div className="space-y-3">
                                    <InfoRow label="Token Name" value={tokenInfo.name} />
                                    <InfoRow label="Symbol" value={`$${tokenInfo.symbol}`} />
                                    <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                        <span className="text-muted-foreground">Official Logo</span>
                                        <div className="h-12 w-12 rounded-full overflow-hidden shadow-md shadow-primary/10">
                                            <Image src="/token_logo.png" alt="TKS Token" width={48} height={48} className="w-full h-full object-cover" unoptimized />
                                        </div>
                                    </div>
                                    <InfoRow label="Decimals" value={String(tokenInfo.decimals)} />
                                    <InfoRow label="Total Supply" value={`${tokenInfo.totalSupply} TKS`} />
                                    <div className="p-4 rounded-xl bg-muted/30">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-muted-foreground flex items-center gap-2">
                                                <Globe className="h-4 w-4" />
                                                Deployed Networks
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {tokenInfo.networks.map((n) => (
                                                <span key={n} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                                    {n}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="flex justify-between items-center mb-2 mt-4">
                                            <span className="text-muted-foreground text-sm">Upcoming</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {tokenInfo.upcomingNetworks.map((n) => (
                                                <span key={n} className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">
                                                    {n}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div variants={fadeUp} className="glass-card p-8">
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                    <Layers className="h-5 w-5 text-primary" />
                                    Token Utility
                                </h3>
                                <div className="space-y-4">
                                    {[
                                        { icon: Coins, title: "Platform Fees", desc: "Pay reduced fees when using TKS for token creation, presales, and locking" },
                                        { icon: TrendingUp, title: "Staking Rewards", desc: "Stake TKS to earn passive income with duration-based APY" },
                                        { icon: Vote, title: "Governance", desc: "Vote on platform proposals and shape the future of TokenKickstarter" },
                                        { icon: Shield, title: "Priority Access", desc: "Get early access to new presales and exclusive platform features" },
                                    ].map((u) => {
                                        const UIcon = u.icon
                                        return (
                                            <div key={u.title} className="flex gap-4 p-4 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all">
                                                <div className="h-12 w-12 shrink-0 flex items-center justify-center rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-all">
                                                    <UIcon className="h-6 w-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold mb-1">{u.title}</h4>
                                                    <p className="text-sm text-muted-foreground">{u.desc}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 3. TOKENOMICS ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="tokenomics"
                            icon={BarChart3}
                            badge="Section 3"
                            title="Token"
                            titleHighlight="Distribution"
                            description="1 billion TKS tokens strategically allocated to ensure long-term sustainability."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-3xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="space-y-5">
                                {tokenomics.map((t) => (
                                    <div key={t.label}>
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-medium">{t.label}</span>
                                            <span className="text-primary font-bold">{t.pct}%</span>
                                        </div>
                                        <div className="h-3 rounded-full bg-muted/40 overflow-hidden">
                                            <motion.div
                                                className={`h-full rounded-full ${t.color}`}
                                                initial={{ width: 0 }}
                                                whileInView={{ width: `${t.pct}%` }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 pt-6 border-t border-border/30 grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                                <div className="p-4 rounded-xl bg-muted/30">
                                    <div className="text-2xl font-bold text-primary">1B</div>
                                    <div className="text-xs text-muted-foreground mt-1">Total Supply</div>
                                </div>
                                <div className="p-4 rounded-xl bg-muted/30">
                                    <div className="text-2xl font-bold text-primary">18</div>
                                    <div className="text-xs text-muted-foreground mt-1">Decimals</div>
                                </div>
                                <div className="p-4 rounded-xl bg-muted/30 col-span-2 sm:col-span-1">
                                    <div className="text-2xl font-bold text-primary">ERC-20</div>
                                    <div className="text-xs text-muted-foreground mt-1">Standard</div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 4. STAKING ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="staking"
                            icon={TrendingUp}
                            badge="Section 4"
                            title="Staking"
                            titleHighlight="Program"
                            description="Earn passive income by staking TKS with duration-based reward tiers."
                        />
                        <motion.div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            {stakingTiers.map((tier) => (
                                <motion.div key={tier.duration} variants={fadeUp} className="glass-card p-6 text-center group hover:border-primary/40 transition-all">
                                    <div className="text-3xl font-bold text-primary mb-1">{tier.apy}</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-4">APY</div>
                                    <div className="h-px bg-border/30 mb-4" />
                                    <div className="font-semibold mb-1">{tier.duration}</div>
                                    <div className="text-sm text-muted-foreground">Min: {tier.min}</div>
                                </motion.div>
                            ))}
                        </motion.div>

                        <motion.div
                            className="glass-card p-8 mt-8 max-w-5xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <h3 className="font-bold text-lg mb-4">Staking Features</h3>
                            <div className="grid gap-4 sm:grid-cols-2">
                                {[
                                    "Stake TKS, LP tokens, or stablecoins",
                                    "Higher APY for longer commitment periods",
                                    "Auto-compounding reward mechanism",
                                    "No lock-up penalty — withdraw any time (forfeits bonus)",
                                    "Real-time reward tracking dashboard",
                                    "Multi-chain staking support",
                                ].map((feat, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm">
                                        <Zap className="h-4 w-4 text-primary shrink-0" />
                                        <span className="text-muted-foreground">{feat}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 5. CREATE PRESALE ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="create-presale"
                            icon={Rocket}
                            badge="Section 5"
                            title="Create"
                            titleHighlight="Presale"
                            description="Launch your own token presale with smart-contract powered fundraising."
                        />
                        <motion.div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <FeatureCard icon={TrendingUp} title="Chainlink Price Feeds" description="Real-time USD conversion using Chainlink oracles for accurate contribution pricing across all currencies." />
                            <FeatureCard icon={Globe} title="Multi-Currency" description="Accept contributions in native tokens (ETH, BNB, MATIC) and stablecoins (USDT, USDC, DAI)." />
                            <FeatureCard icon={Users} title="Referral System" description="Built-in 5% referral bonus that incentivizes community-driven growth and token distribution." />
                            <FeatureCard icon={Shield} title="Configurable Caps" description="Set customizable soft/hard caps, min/max contribution limits, and presale duration." />
                            <FeatureCard icon={Lock} title="Auto Liquidity Lock" description="Automatic liquidity locking after presale completion, protecting investor funds." />
                            <FeatureCard icon={Zap} title="Instant Treasury" description="Contributions transfer directly to the project wallet — no locked or delayed funds." />
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 6. CREATE TOKEN ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="create-token"
                            icon={Code2}
                            badge="Section 6"
                            title="Create"
                            titleHighlight="Token"
                            description="No-code ERC-20 token factory with advanced features built in."
                        />
                        <motion.div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <FeatureCard icon={Coins} title="Custom Tokenomics" description="Set your own supply, name, symbol, and decimal configuration with a simple form." />
                            <FeatureCard icon={Shield} title="Anti-Whale Protection" description="Built-in max transaction and max wallet limits to prevent token concentration." />
                            <FeatureCard icon={TrendingUp} title="Tax System" description="Configure buy/sell taxes that fund marketing, development, or auto-liquidity." />
                            <FeatureCard icon={Zap} title="Auto-Liquidity" description="Automatic liquidity pool generation from transaction taxes." />
                            <FeatureCard icon={Lock} title="Ownership Controls" description="Renounce ownership, transfer, or retain control — fully customizable." />
                            <FeatureCard icon={FileCode} title="Verified Source" description="All deployed contracts are automatically verified on block explorers." />
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 7. TOKEN LOCKING ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="token-locking"
                            icon={Lock}
                            badge="Section 7"
                            title="Token"
                            titleHighlight="Locking"
                            description="Secure tokens and LP with time-locked or vesting schedules to build investor trust."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="grid gap-6 md:grid-cols-2">
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg flex items-center gap-2">
                                        <Lock className="h-5 w-5 text-primary" />
                                        Time Lock
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Lock tokens for a fixed duration. Tokens are securely held in a smart contract
                                        and automatically released when the lock period expires. Perfect for team tokens,
                                        marketing allocations, and LP tokens.
                                    </p>
                                    <div className="space-y-2">
                                        {["Customizable lock duration", "Auto-release on expiry", "On-chain proof of lock", "Multi-token support"].map((f, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-muted-foreground">{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5 text-primary" />
                                        Vesting Schedule
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Release tokens gradually over time with a configurable vesting schedule.
                                        Set cliff periods, linear release rates, and milestone-based distributions
                                        for a structured token unlock.
                                    </p>
                                    <div className="space-y-2">
                                        {["Cliff + linear vesting", "Milestone-based release", "Revocable or irrevocable", "Transparent on-chain schedule"].map((f, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-muted-foreground">{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 8. LISTING DIRECTORY ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="listing-directory"
                            icon={Search}
                            badge="Section 8 — Upcoming"
                            title="Token Listing"
                            titleHighlight="Directory"
                            description="A comprehensive directory for discovering tokens and presales across all supported chains."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="flex items-start gap-4 mb-6">
                                <div className="h-12 w-12 shrink-0 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                                    <Search className="h-6 w-6 text-amber-500" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="font-bold text-lg">Coming Soon</h3>
                                        <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold">UPCOMING</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        The Token Listing Directory will be a discovery hub for all tokens created
                                        and presales launched through TokenKickstarter. Users will be able to search,
                                        filter, and explore projects with detailed analytics, social metrics, and
                                        audit status.
                                    </p>
                                </div>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                                {[
                                    "Search & filter by chain, category, and status",
                                    "Real-time price and volume data",
                                    "KYC & audit verification badges",
                                    "Community ratings and reviews",
                                    "Trending and featured listings",
                                    "Integration with DEX aggregators",
                                ].map((feat, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm p-3 rounded-lg bg-muted/30">
                                        <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0" />
                                        <span className="text-muted-foreground">{feat}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 9. SMART CONTRACTS ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="smart-contracts"
                            icon={FileCode}
                            badge="Section 9"
                            title="Smart"
                            titleHighlight="Contracts"
                            description="All deployed contract addresses across mainnet and testnet networks."
                        />
                        <motion.div
                            className="glass-card p-8 max-w-5xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            {/* ─── Mainnet ─── */}
                            <div className="mb-8">
                                <div className="flex items-center gap-2 mb-5">
                                    <span className="px-2.5 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-semibold uppercase tracking-wider">
                                        Mainnet
                                    </span>
                                </div>
                                <div className="grid gap-6 lg:grid-cols-2">
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">TKS Token</p>
                                        <div className="space-y-1.5">{contracts.mainnet.token.map((c) => <AddressRow key={`mt-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Claim Vault <span className="text-xs opacity-60">(UUPS Proxy)</span></p>
                                        <div className="space-y-1.5">{contracts.mainnet.vault.map((c) => <AddressRow key={`mv-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Contribution Collector</p>
                                        <div className="space-y-1.5">{contracts.mainnet.collector.map((c) => <AddressRow key={`mc-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Staking Contract</p>
                                        <div className="space-y-1.5">{contracts.mainnet.staking.map((c) => <AddressRow key={`ms-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Presale Factory</p>
                                        <div className="space-y-1.5">{contracts.mainnet.presaleFactory.map((c) => <AddressRow key={`mpf-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Locker</p>
                                        <div className="space-y-1.5">{contracts.mainnet.tokenLocker.map((c) => <AddressRow key={`ml-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Deployer Proxy</p>
                                        <div className="space-y-1.5">{contracts.mainnet.deployerProxy.map((c) => <AddressRow key={`mdp-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Fair Launch Factory</p>
                                        <div className="space-y-1.5">{contracts.mainnet.fairLaunchFactory.map((c) => <AddressRow key={`mfl-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Collector Factory</p>
                                        <div className="space-y-1.5">{contracts.mainnet.collectorFactory.map((c) => <AddressRow key={`mcf-${c.network}`} {...c} />)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* ─── Testnet ─── */}
                            <div>
                                <div className="flex items-center gap-2 mb-5">
                                    <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold uppercase tracking-wider">
                                        Testnet
                                    </span>
                                </div>
                                <div className="grid gap-6 lg:grid-cols-2">
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">TKS Token</p>
                                        <div className="space-y-1.5">{contracts.testnet.token.map((c) => <AddressRow key={`tt-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Claim Vault <span className="text-xs opacity-60">(UUPS Proxy)</span></p>
                                        <div className="space-y-1.5">{contracts.testnet.vault.map((c) => <AddressRow key={`tv-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Contribution Collector</p>
                                        <div className="space-y-1.5">{contracts.testnet.collector.map((c) => <AddressRow key={`tc-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Staking Contract</p>
                                        <div className="space-y-1.5">{contracts.testnet.staking.map((c) => <AddressRow key={`ts-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Presale Factory</p>
                                        <div className="space-y-1.5">{contracts.testnet.presaleFactory.map((c) => <AddressRow key={`tpf-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Locker</p>
                                        <div className="space-y-1.5">{contracts.testnet.tokenLocker.map((c) => <AddressRow key={`tl-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Deployer Proxy</p>
                                        <div className="space-y-1.5">{contracts.testnet.deployerProxy.map((c) => <AddressRow key={`tdp-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Fair Launch Factory</p>
                                        <div className="space-y-1.5">{contracts.testnet.fairLaunchFactory.map((c) => <AddressRow key={`tfl-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Collector Factory</p>
                                        <div className="space-y-1.5">{contracts.testnet.collectorFactory.map((c) => <AddressRow key={`tcf-${c.network}`} {...c} />)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Contract Architecture */}
                            <div className="mt-8 pt-6 border-t border-border/30">
                                <h3 className="font-bold text-lg mb-4">Contract Architecture</h3>
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                                        <div className="text-xl font-bold text-primary mb-1">OpenZeppelin</div>
                                        <div className="text-xs text-muted-foreground">Battle-Tested Libraries</div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                                        <div className="text-xl font-bold text-primary mb-1">UUPS</div>
                                        <div className="text-xs text-muted-foreground">Upgradeable Proxy Pattern</div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                                        <div className="text-xl font-bold text-primary mb-1">Chainlink</div>
                                        <div className="text-xs text-muted-foreground">Oracle Price Feeds</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 10. ARCHITECTURE ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="architecture"
                            icon={Layers}
                            badge="Section 10"
                            title="Technical"
                            titleHighlight="Architecture"
                            description="A multi-layered architecture designed for scalability, security, and cross-chain interoperability."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="space-y-8">
                                {[
                                    {
                                        title: "Smart Contract Layer",
                                        icon: FileCode,
                                        desc: "Solidity contracts deployed on EVM-compatible chains. Upgradeable via UUPS proxy pattern. Built on OpenZeppelin's audited libraries for maximum security.",
                                        tags: ["Solidity", "UUPS Proxy", "OpenZeppelin", "Hardhat"],
                                    },
                                    {
                                        title: "Oracle Integration",
                                        icon: TrendingUp,
                                        desc: "Chainlink price feeds provide tamper-proof, decentralized price data for real-time USD conversion during presale contributions.",
                                        tags: ["Chainlink", "Price Feeds", "ETH/USD", "BNB/USD"],
                                    },
                                    {
                                        title: "Multi-Chain Support",
                                        icon: Globe,
                                        desc: "Native support for BSC and Ethereum with expansion planned for Base and Polygon. Cross-chain token bridging and claim mechanisms.",
                                        tags: ["BSC", "Ethereum", "Base", "Polygon"],
                                    },
                                    {
                                        title: "Frontend & API",
                                        icon: Code2,
                                        desc: "Next.js 16 frontend with server-side rendering, Reown AppKit for multi-wallet connectivity, and RESTful API routes for off-chain operations.",
                                        tags: ["Next.js 16", "React 19", "AppKit", "Prisma"],
                                    },
                                ].map((layer) => {
                                    const LIcon = layer.icon
                                    return (
                                        <div key={layer.title} className="flex gap-5 p-5 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all">
                                            <div className="h-12 w-12 shrink-0 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-all">
                                                <LIcon className="h-6 w-6 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold mb-1">{layer.title}</h4>
                                                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{layer.desc}</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {layer.tags.map((tag) => (
                                                        <span key={tag} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 11. REVENUE & ECOSYSTEM ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="revenue"
                            icon={CircleDollarSign}
                            badge="Section 11"
                            title="Revenue &"
                            titleHighlight="Ecosystem"
                            description="A self-sustaining ecosystem where multiple revenue streams continuously increase TKS value."
                        />
                        <div className="max-w-5xl mx-auto space-y-8">
                            {/* Revenue Sources */}
                            <motion.div
                                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                                variants={staggerContainer}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <FeatureCard icon={Building2} title="Subsidiary Revenue" description="TokenKickstarter establishes strategic subsidiary ventures. All proceeds are reinvested back into the ecosystem." />
                                <FeatureCard icon={Coins} title="Platform Fees" description="Revenue from token creation, presale launches, token locking, and premium features." />
                                <FeatureCard icon={TrendingUp} title="Trading Fees" description="Nominal fees on ecosystem transactions contribute to the treasury." />
                                <FeatureCard icon={Megaphone} title="Marketing Investment" description="Strategic marketing spend to drive adoption, partnerships, and brand awareness." />
                                <FeatureCard icon={Code2} title="Product Development" description="Continuous investment in new DeFi features, tools, and cross-chain expansion." />
                                <FeatureCard icon={CircleDollarSign} title="Buy-Back & Burn" description="Ecosystem revenue used for periodic buy-back and burn events to reduce supply and increase value." />
                            </motion.div>

                            {/* Revenue Flow */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="font-bold text-xl mb-6 text-center">Revenue Cycle</h3>
                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                    {[
                                        { step: 1, title: "Generate", desc: "Subsidiary ventures & platform fees generate revenue" },
                                        { step: 2, title: "Reinvest", desc: "Revenue funneled into marketing & development" },
                                        { step: 3, title: "Grow", desc: "Ecosystem expansion increases token utility" },
                                        { step: 4, title: "Appreciate", desc: "Buy-back & burn increases TKS value" },
                                    ].map((item, i) => (
                                        <div key={item.step} className="relative">
                                            <div className="flex flex-col items-center text-center p-6 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all">
                                                <div className="h-14 w-14 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-xl mb-4 group-hover:scale-110 transition-all">
                                                    {item.step}
                                                </div>
                                                <h4 className="font-bold mb-1">{item.title}</h4>
                                                <p className="text-sm text-muted-foreground">{item.desc}</p>
                                            </div>
                                            {i < 3 && (
                                                <div className="hidden lg:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                                                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}
